import React, { useState } from "react";
import {
    Button,
    Col,
    FormCheck,
    FormGroup,
    FormLabel,
    Modal,
    Row,
} from "react-bootstrap";
import FormCheckLabel from "react-bootstrap/esm/FormCheckLabel";
import Select from "react-select";

import classNames from "classnames";

import { alertActions } from "../../features/alert/alertSlice";
import { selectSelectedTournament } from "../../features/tourneymgmt/tournamentManagementSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
    assignPlayoffSeeds,
    clearPlaceholderSchedule,
    generatePlaceholderSchedule,
    generateTournamentSchedule,
    resetPlayoffSeeds,
    resetTournamentSchedule,
} from "../../lib/api";
import { isChildTournament } from "../../lib/utils";

const PlayoffButtonsModal = ({
    action,
    tournamentId,
    showModal,
    setShowModal,
}: {
    action:
        | "assign"
        | "reset"
        | "pool"
        | "clear"
        | "placeholder"
        | "clearplaceholder";
    tournamentId: number;
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(state => state.auth.user);
    const tournament = useAppSelector(selectSelectedTournament);
    const tournaments = useAppSelector(
        state => state.tournamentmanagement.tournaments,
    );
    const isMasterTournament = !isChildTournament(tournament);

    const childTournamentList = isMasterTournament
        ? tournaments.filter(t => t.parent === tournament?.id)
        : [];
    const [childTournament, setChildTournament] = useState<number>(0);
    const [assignSeedsIgnoreGames, setAssignSeedsIgnoreGames] =
        useState<boolean>(false);

    const handleAction = async (
        action:
            | "assign"
            | "reset"
            | "pool"
            | "clear"
            | "placeholder"
            | "clearplaceholder",
        tournamentId: number,
    ) => {
        switch (action) {
            case "assign":
                assignPlayoffSeeds(
                    isMasterTournament ? childTournament : tournamentId,
                    assignSeedsIgnoreGames,
                );
                dispatch(
                    alertActions.success({
                        title: `Bracket seeding has started`,
                        text: `Please wait up to 60 seconds for all seeds to be assigned before checking bracket seedings/schedule`,
                        dismissible: true,
                    }),
                );
                break;
            case "reset":
                resetPlayoffSeeds(tournamentId, user!.id)
                    .then(() => {
                        dispatch(
                            alertActions.success({
                                title: `Bracket seeds reset`,
                                dismissible: true,
                            }),
                        );
                    })
                    .catch(e => {
                        dispatch(
                            alertActions.error({
                                title: `The was a problem resetting bracket seeds.`,
                                text: e,
                                dismissible: true,
                            }),
                        );
                    });
                break;
            case "pool":
                generateTournamentSchedule(tournamentId, user!.id);
                dispatch(
                    alertActions.success({
                        title: `Tournament schedule generation has started`,
                        text: `Please wait up to 60 seconds for all games to be generated before checking the schedule/calendar`,
                        dismissible: true,
                    }),
                );
                break;
            case "placeholder":
                generatePlaceholderSchedule(tournamentId, user!.id);
                dispatch(
                    alertActions.success({
                        title: `Plceholder generation has started`,
                        text: `Please wait up to 60 seconds for all games to be generated before checking the schedule/calendar`,
                        dismissible: true,
                    }),
                );
                break;
            case "clearplaceholder":
                clearPlaceholderSchedule(tournamentId, user!.id);
                dispatch(
                    alertActions.success({
                        title: `Placeholder Schedule has been cleared`,
                        text: `Please wait up to 60 seconds for all games to be generated before checking the schedule/calendar`,
                        dismissible: true,
                    }),
                );
                break;
            case "clear":
                resetTournamentSchedule(tournamentId, user!.id)
                    .then(() => {
                        dispatch(
                            alertActions.success({
                                title: `Tournament schedule reset`,
                                dismissible: true,
                            }),
                        );
                    })
                    .catch(e => {
                        dispatch(
                            alertActions.error({
                                title: `The was a problem resetting the tournament schedule.`,
                                text: e,
                                dismissible: true,
                            }),
                        );
                    });
                break;
        }

        setShowModal(false);
    };

    const buildModalBody = () => {
        const modalBodies: { [key: string]: JSX.Element } = {
            assign: (
                <React.Fragment>
                    {isMasterTournament && (
                        <Row className={classNames("mb-2")}>
                            <Col xs={4}>
                                <b>Select Age Level:</b>
                            </Col>
                            <Col>
                                <Select
                                    isMulti={false}
                                    isClearable={false}
                                    isSearchable={false}
                                    options={childTournamentList.map(t => {
                                        return {
                                            label: t.ageLevel,
                                            value: t.id,
                                        };
                                    })}
                                    onChange={v =>
                                        v && setChildTournament(v?.value)
                                    }
                                />
                            </Col>
                        </Row>
                    )}
                    {(!isMasterTournament || childTournament !== 0) && (
                        <Row>
                            <Col>
                                <p>
                                    Are you sure you want to generate the
                                    brackets for this tournament and assign
                                    teams to their seeds?
                                </p>
                                <p>
                                    This process can take up to a minute to
                                    generate and is executed in the background.
                                </p>
                                <p>
                                    You can continue to use the site while the
                                    bracket is building.
                                </p>
                                <FormGroup>
                                    <FormCheck
                                        id="ignoreGames"
                                        inline
                                        type="checkbox"
                                        onChange={e =>
                                            setAssignSeedsIgnoreGames(
                                                e.target.checked,
                                            )
                                        }
                                    />
                                    <FormLabel htmlFor="ignoreGames">
                                        Ignore played games/standings?
                                    </FormLabel>
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                </React.Fragment>
            ),
            reset: (
                <>
                    <h5 className="text-danger text-center">
                        Are you sure you want to reset the brackets for this
                        tournament?
                    </h5>
                    <h6 className="text-danger text-center">
                        This will remove all currently assigned seeds for
                        bracket games in this tournament!
                    </h6>
                </>
            ),
            pool: (
                <>
                    <p>
                        Are you sure you want to generate the full schedule for
                        this tournament?
                    </p>
                    <p>
                        This process can take up to a minute to generate and is
                        executed in the background.
                    </p>
                    <p>
                        You can continue to use the site while the schedule is
                        being generated.
                    </p>
                </>
            ),
            placeholder: (
                <>
                    <p>
                        Are you sure you want to generate the placeholder games
                        for this for this tournament?
                    </p>
                    <p>
                        This process can take up to a minute to generate and is
                        executed in the background.
                    </p>
                    <p>
                        You can continue to use the site while the schedule is
                        being generated.
                    </p>
                </>
            ),
            clearplaceholder: (
                <>
                    <p>
                        Are you sure you want to clear the placeholder games for
                        this for this tournament?
                    </p>
                    <p className={classNames("text-danger")}>
                        <b>
                            This will remove all placeholder games for this
                            tournament including any registered umpires. Use
                            with caution!
                        </b>
                    </p>
                    <p>
                        You can continue to use the site while the schedule is
                        being generated.
                    </p>
                </>
            ),
            clear: (
                <>
                    <h5 className="text-danger text-center">
                        Are you sure you want to reset the schedule for this
                        tournament?
                    </h5>
                    <h6 className="text-danger text-center">
                        This will remove all currently assigned pool and bracket
                        games in this tournament (and all child tournaments)!
                    </h6>
                </>
            ),
        };

        return modalBodies[action] || null;
    };

    const buildModalHeader = () => {
        const modalHeaders: { [key: string]: string } = {
            assign: "Assign Bracket Seeds",
            reset: "Reset Bracket Seeds",
            pool: "Generate Pool Games",
            clear: "Clear Tournament Schedule",
            placeholder: "Generate Placeholder Schedule",
            clearplaceholder: "Clear Placeholder Schedule",
        };

        return modalHeaders[action] || null;
    };

    return (
        <Modal
            show={showModal}
            onShow={() => {
                setChildTournament(0);
            }}
            onHide={() => {
                setShowModal(false);
            }}
        >
            <Modal.Header>
                <Modal.Title>{buildModalHeader()}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{buildModalBody()}</Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => setShowModal(false)}>
                    No
                </Button>
                <Button
                    variant="danger"
                    disabled={
                        action === "assign" &&
                        isMasterTournament &&
                        childTournament === 0
                    }
                    onClick={() => {
                        handleAction(action, tournamentId);
                    }}
                >
                    Yes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PlayoffButtonsModal;
